import { Close } from "@mui/icons-material";
import {
  Dialog,
  DialogContent,
  Grid2,
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { OccupiedStatus } from "../../../../Domain/Types/Status";
import useTimeFormat from "../../../../../../hooks/useTimeFormat/useTimeFormat";
import { DefaultUserAvatar } from "../../../../../UserAccount/default-avatar.component";
import { BasicUserInfo } from "../../../../../../features/Reports/typings/occupation-report";
import { TFunction } from "i18next";

interface Props {
  isDialogOpen: boolean;
  setIsDialogOpen: (o: boolean) => void;
  timezone: string;
  occupiedStatus: OccupiedStatus[] | undefined;
}

export function PlaceBookingDetailDialog({
  isDialogOpen,
  setIsDialogOpen,
  timezone = "Europe/Berlin",
  occupiedStatus
}: Readonly<Props>) {
  const { t } = useTranslation();
  const { dateFormat } = useTimeFormat();

  const hideMuiScroll = {
    "&:hover::-webkit-scrollbar": {
      display: "none"
    },
    "&:click::-webkit-scrollbar": {
      display: "none"
    },
    "&::-webkit-scrollbar": {
      display: "none"
    }
  };

  return (
    <Dialog open={isDialogOpen} data-testid={"place-booking-detail-modal"}>
      <DialogContent>
        <Stack gap={1} data-testid={"place-booking-detail-stack"}>
          <Grid2 container>
            <Grid2 size={"grow"} />
            <Grid2 size={{ xs: 10 }}>
              <Typography align={"center"} lineHeight={2} variant={"h5"}>
                {t("Bookings")}
              </Typography>
            </Grid2>
            <Grid2 size={{ xs: 1 }} sx={{ textAlign: "right", placeSelf: "center" }}>
              <IconButton
                sx={{ float: "right" }}
                data-testid={"close-detail-modal"}
                onClick={() => setIsDialogOpen(false)}
                size="large"
              >
                <Close />
              </IconButton>
            </Grid2>
          </Grid2>
          <Grid2 container>
            <TableContainer
              data-testid="place-booking-detail-tb-con"
              sx={{
                maxHeight: "70vh",
                maxWidth: "60vw",
                ...hideMuiScroll
              }}
            >
              <Table>
                <caption style={{ textAlign: "end", padding: "10px" }}>
                  {timezone
                    ? `${t("Dates are in timezone:")} ${timezone} (${DateTime.now()
                        .setZone(timezone)
                        .toFormat("ZZZZ")})`
                    : t("Dates are in timezone: UTC")}
                </caption>
                <TableHead>
                  <TableRow>
                    <TableCell>{t("User")}</TableCell>
                    <TableCell>{t("Date")}</TableCell>
                    <TableCell>{t("From")}</TableCell>
                    <TableCell>{t("Until")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {occupiedStatus?.map(row => (
                    <TableRow key={row.userInfo.userId}>
                      <TableCell>{userAvatar(row.userInfo, t)}</TableCell>
                      <TableCell>{DateTime.fromISO(row.startTime).toFormat(dateFormat)}</TableCell>
                      <TableCell>{DateTime.fromISO(row.startTime).toFormat("HH:mm")}</TableCell>
                      <TableCell>{DateTime.fromISO(row.endTime).toFormat("HH:mm")}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid2>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

export function userAvatar(userInfo: BasicUserInfo, t: TFunction) {
  const emailHref = (e: string) => {
    return "mailto:" + e;
  };

  const nameAndEmail = `${userInfo.firstName ?? ""} ${userInfo.surname ?? t("No name")} (${
    userInfo.email
  })`;

  function checkEmail(userInfo: BasicUserInfo) {
    if (userInfo.email)
      return (
        <Link color="inherit" href={emailHref(userInfo.email)}>
          {nameAndEmail}
        </Link>
      );
    else
      return (
        <Typography>{`${userInfo.firstName ?? ""} ${userInfo.surname ?? t("No Name")}`}</Typography>
      );
  }

  return (
    <Grid2 container sx={{ width: "max-content" }}>
      <Grid2 sx={{ width: "40px", placeSelf: "center" }}>
        {userInfo.isAnonymus ? (
          <DefaultUserAvatar />
        ) : (
          <DefaultUserAvatar
            firstName={userInfo.firstName}
            surname={userInfo.surname}
            iconUrl={userInfo.photoUrl}
          />
        )}
      </Grid2>
      <Grid2 sx={{ alignSelf: "center", marginLeft: "15px", width: "min-content" }}>
        {userInfo.isAnonymus ? t("Anonymous") : checkEmail(userInfo)}
      </Grid2>
    </Grid2>
  );
}
